import { RefObject, useEffect, useState } from 'react';
// @mui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import LoadingButton from '@mui/lab/LoadingButton';
import Portal from '@mui/material/Portal';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Theme } from '@mui/material/styles';
import Slide from '@mui/material/Slide';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
//
import Iconify from 'src/components/iconify';
import Label from 'src/components/label';
import { Issue } from './automatic-review';

// ----------------------------------------------------------------------

type Props = {
  issue: Issue;
  // Desktop: rendered inline into this host, replacing the wizard header.
  // Without a host, or on small screens, it floats fixed over the page.
  host?: RefObject<HTMLDivElement>;
  // Live truth from the saved filing: does this issue still show up?
  resolved: boolean;
  // "This is correct" state, shared with the review tab and persisted.
  acknowledged: boolean;
  onAcknowledge: (value: boolean) => void;
  hasNext: boolean;
  onNext: () => void;
  onClose: () => void;
};

// The issue the user is currently fixing, pinned over the page header so it
// stays in sight while they work in the Miles/Fuel tabs.
export default function PinnedIssueCard({ issue, host, resolved, acknowledged, onAcknowledge, hasNext, onNext, onClose }: Props) {
  const [checking, setChecking] = useState(false);
  const [status, setStatus] = useState<'none' | 'resolved' | 'still'>('none');

  useEffect(() => {
    setStatus('none');
    setChecking(false);
  }, [issue.id]);

  const recheck = () => {
    setChecking(true);
    setTimeout(() => {
      setChecking(false);
      setStatus(resolved ? 'resolved' : 'still');
    }, 700);
  };

  const isResolved = status === 'resolved';
  // Green treatment both when the data no longer trips the check and when the
  // user marked it as correct.
  const success = isResolved || acknowledged;

  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const inline = mdUp && Boolean(host?.current);

  return (
    <Portal container={inline ? host!.current : undefined}>
    <Box
      sx={
        inline
          ? { width: 1 }
          : {
              position: 'fixed',
              top: 12,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1250,
              width: 'min(92vw, 720px)',
            }
      }
      data-testid="pinned-issue-card"
    >
      <Slide direction="down" in>
        <Card
          sx={{
            p: 2.5,
            borderRadius: '12px',
            border: '1px solid',
            borderColor: success ? 'success.main' : '#F0F3F0',
            boxShadow: inline
              ? '0 10px 30px -14px rgba(1, 14, 7, 0.08)'
              : '0 16px 40px -12px rgba(1, 14, 7, 0.24)',
          }}
        >
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Iconify
                icon={success ? 'eva:checkmark-circle-2-fill' : 'solar:danger-triangle-bold'}
                width={20}
                sx={{ color: success ? 'success.main' : '#F5523C' }}
              />
              <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                Fixing issue
              </Typography>
              {isResolved && (
                <Label color="success" data-testid="pinned-status">
                  Resolved
                </Label>
              )}
              {status === 'still' && !acknowledged && (
                <Label color="error" data-testid="pinned-status">
                  Still needs attention
                </Label>
              )}
              <Box sx={{ flexGrow: 1 }} />
              <IconButton size="small" onClick={onClose} aria-label="Close" data-testid="pinned-close">
                <Iconify icon="mingcute:close-line" width={18} />
              </IconButton>
            </Stack>

            <Typography variant="body2">
              {isResolved
                ? 'Nice! This issue no longer shows up in your saved data.'
                : issue.text}
            </Typography>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ flexWrap: 'wrap', rowGap: 1, columnGap: 1 }}
            >
              {isResolved ? (
                <Box />
              ) : (
                <Button
                  size="small"
                  variant={acknowledged ? 'contained' : 'outlined'}
                  color={acknowledged ? 'success' : 'inherit'}
                  startIcon={
                    acknowledged ? <Iconify icon="eva:checkmark-fill" width={16} /> : undefined
                  }
                  onClick={() => onAcknowledge(!acknowledged)}
                  sx={{ whiteSpace: 'nowrap', ...(acknowledged && { color: '#FFFFFF' }) }}
                  data-testid="pinned-ack"
                >
                  This is correct
                </Button>
              )}
              <Stack direction="row" spacing={1} alignItems="center">
                {!acknowledged && (
                  <LoadingButton
                    size="small"
                    variant="outlined"
                    color="inherit"
                    loading={checking}
                    onClick={recheck}
                    startIcon={<Iconify icon="solar:refresh-bold" width={16} />}
                    sx={{ whiteSpace: 'nowrap' }}
                    data-testid="pinned-recheck"
                  >
                    Recheck
                  </LoadingButton>
                )}
                <Button
                  size="small"
                  variant="contained"
                  color="info"
                  onClick={onNext}
                  sx={{ color: '#FFFFFF', whiteSpace: 'nowrap' }}
                  data-testid="pinned-next"
                >
                  {hasNext ? 'Next issue' : 'Back to review'}
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Card>
      </Slide>
    </Box>
    </Portal>
  );
}
